import DOMPurify from 'dompurify'
import { keepMentionIdsOnly } from 'appUtils/sanitizeRichTextHtml'

// Comment text is shown as HTML to everyone on the task. Keep mentions, links and simple
// formatting; drop anything that could run, load or cover the page (scripts, handlers, images,
// forms, styles).
const ALLOWED_TAGS = [
  'mention',
  'span',
  'a',
  'br',
  'p',
  'div',
  'b',
  'strong',
  'i',
  'em',
  'u',
  's',
  'ul',
  'ol',
  'li',
  'code',
  'pre',
]
const ALLOWED_ATTR = ['id', 'class', 'href', 'target', 'rel']

// Only the classes formatContent adds itself, so a comment can't restyle or cover the page.
const OWN_CLASSES = new Set([
  'text-indigo-600',
  'text-white',
  'font-medium',
  'underline',
  'hover:text-app-white-95',
  'text-app-primary-125',
  'hover:underline',
])

const keepOwnAttributes = (
  node: Element,
  data: { attrName: string; attrValue: string; keepAttr: boolean }
) => {
  // Only a mention's id, when it names a person or Coach: any other id could stand in for one of
  // the page's own elements (e.g. the comment upload field's input, found by its id).
  if (data.attrName === 'id') {
    keepMentionIdsOnly(node, data)
    return
  }
  if (data.attrName !== 'class') return
  const tokens = data.attrValue.split(/\s+/).filter(Boolean)
  if (!tokens.every((token) => OWN_CLASSES.has(token))) data.keepAttr = false
}

export const sanitizeCommentHtml = (html: string): string => {
  DOMPurify.addHook('uponSanitizeAttribute', keepOwnAttributes as any)
  try {
    // DOMPurify keeps data-* and aria-* attributes unless told not to, and some of them act:
    // react-tooltip renders an element's data-tooltip-html as HTML on hover.
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS,
      ALLOWED_ATTR,
      ALLOW_DATA_ATTR: false,
      ALLOW_ARIA_ATTR: false,
    })
  } finally {
    DOMPurify.removeHook('uponSanitizeAttribute')
  }
}
